/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { SEO, ButtonLink, ScrollReveal, ArrowRightIcon } from '../components/common';
import { BlogCard } from '../components/blog';
import {
  HeroSection,
  StatsSection,
  ServicesSection,
  HowItWorksSection,
  AppPromoSection,
  AppModal,
  TestimonialsSection,
  FAQSection,
} from '../components/home';
import { blogPosts, getFeaturedPosts } from '../data/blogPosts';
import { colors, borderRadius, shadows, typography } from '../styles/theme';

const sectionStyles = css`
  max-width: 1200px;
  margin: 0 auto;
  padding: 3rem 1rem;

  @media (min-width: 768px) {
    padding: 4rem 2rem;
  }
`;

const sectionHeaderStyles = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;

  h2 {
    font-family: ${typography.fontFamily.heading};
    font-size: ${typography.fontSize['2xl']};
    color: ${colors.text.primary};
  }

  a {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    flex-shrink: 0;
    color: ${colors.primary[700]};
    font-weight: ${typography.fontWeight.semibold};
    font-size: ${typography.fontSize.sm};
    text-decoration: none;

    svg {
      width: 16px;
      height: 16px;
    }

    &:hover {
      color: ${colors.primary[700]};
      text-decoration: underline;
    }
  }
`;

// Öne çıkan ve son yazılar tek bir blok gibi dursun diye arada çift boşluk oluşmasın
const featuredSectionStyles = css`
  padding-bottom: 0;

  @media (min-width: 768px) {
    padding-bottom: 0;
  }
`;

const featuredGridStyles = css`
  display: grid;
  gap: 2rem;
`;

const blogGridStyles = css`
  display: grid;
  gap: 2rem;
  grid-template-columns: 1fr;

  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const ctaSectionStyles = css`
  background: linear-gradient(135deg, ${colors.secondary[500]} 0%, ${colors.secondary[700]} 100%);
  border-radius: ${borderRadius['2xl']};
  padding: 3rem 1.5rem;
  text-align: center;
  color: white;
  box-shadow: ${shadows.xl};

  @media (min-width: 768px) {
    padding: 4rem 3rem;
  }

  h2 {
    font-size: ${typography.fontSize['2xl']};
    margin-bottom: 1rem;

    @media (min-width: 768px) {
      font-size: ${typography.fontSize['3xl']};
    }
  }

  p {
    color: rgba(255, 255, 255, 0.95);
    margin-bottom: 2rem;
    max-width: 500px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const ctaButtonStyles = css`
  color: ${colors.secondary[600]};

  &:hover:not(:disabled) {
    color: ${colors.secondary[700]};
  }
`;

export default function HomePage() {
  const featuredPost = getFeaturedPosts()[0];
  // Öne çıkan yazı "Son Yazılar"da tekrar görünmesin
  const recentPosts = blogPosts.filter(post => post.id !== featuredPost?.id).slice(0, 3);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const openModal = () => {
    setIsModalOpen(true);
    setCurrentStep(0);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setIsModalOpen(false);
    document.body.style.overflow = 'auto';
  };

  return (
    <>
      <SEO
        title="Ana Sayfa"
        description="YolSepetiGO - Türkiye'nin en güvenilir çekici, vinç, yol yardım ve nakliye platformu. 7/24 hizmetinizdeyiz."
        keywords={['çekici', 'yol yardım', 'nakliye', 'vinç', 'araç kurtarma', 'yolda kaldım']}
      />

      <HeroSection />

      <ScrollReveal>
        <StatsSection />
      </ScrollReveal>

      <ScrollReveal>
        <ServicesSection />
      </ScrollReveal>

      <ScrollReveal>
        <HowItWorksSection />
      </ScrollReveal>

      <ScrollReveal>
        <AppPromoSection onOpenModal={openModal} />
      </ScrollReveal>

      <ScrollReveal>
        <TestimonialsSection />
      </ScrollReveal>

      {/* Öne Çıkan Yazılar */}
      {featuredPost && (
        <ScrollReveal>
          <section css={[sectionStyles, featuredSectionStyles]}>
            <div css={sectionHeaderStyles}>
              <h2>Öne Çıkan Yazı</h2>
              <Link to="/blog">Tümünü Gör <ArrowRightIcon /></Link>
            </div>
            <div css={featuredGridStyles}>
              <BlogCard post={featuredPost} variant="featured" />
            </div>
          </section>
        </ScrollReveal>
      )}

      {/* Son Yazılar */}
      <ScrollReveal>
        <section css={sectionStyles}>
          <div css={sectionHeaderStyles}>
            <h2>Son Yazılar</h2>
            <Link to="/blog">Tümünü Gör <ArrowRightIcon /></Link>
          </div>
          <div css={blogGridStyles}>
            {recentPosts.map(post => (
              <BlogCard key={post.id} post={post} />
            ))}
          </div>
        </section>
      </ScrollReveal>

      <ScrollReveal>
        <FAQSection />
      </ScrollReveal>

      {/* CTA Section */}
      <ScrollReveal direction="scale">
        <section css={sectionStyles}>
          <div css={ctaSectionStyles}>
            <h2>Yolda mı Kaldınız?</h2>
            <p>YolSepetiGO ile hızlı ve güvenilir çekici hizmeti alın. 7/24 yanınızdayız!</p>
            <ButtonLink href="https://yolsepetigo.com" variant="white" size="lg" css={ctaButtonStyles}>
              Hemen Yardım Çağır
            </ButtonLink>
          </div>
        </section>
      </ScrollReveal>

      <AppModal
        isOpen={isModalOpen}
        onClose={closeModal}
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
      />
    </>
  );
}
